import { Suspense, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link, useParams } from 'react-router-dom'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import { ThemeProvider } from '@material-ui/core/styles'
import { useMediaQuery } from 'react-responsive'
import { observer } from 'mobx-react-lite'
import { useFlags } from 'launchdarkly-react-client-sdk'
import ChatIcon from '@ui-components-3/icons/lib/duotone/MessageText'
import classNames from '@ui-components-3/ui/lib/utils/classNames'
import { DASHBOARD } from 'constants/breakpoints'
import { getContentWithFeatureFlags } from 'components/chartConfig'
import overrideTheme from 'theming/overrides/dashboard/tabPanel'
import useVisit from 'hooks/useVisit'

const TABCONTENT_CLASSNAMES = {
  visits: 'flex flex-col no-wrap flex-auto justify-center',
  clientCard: 'p-10 bg-primary-100',
  intake: 'flex flex-col grow',
  safetyPlan: 'p-5 md:py-10 md:px-20 bg-[#F9FBFD]',
}

const TabPanel = ({
  visitType,
  areAdditionalTabsVisible,
  toggleChat,
  chatDialogVisible,
  hideChat,
  modality,
  inProgress,
}) => {
  const flags = useFlags()
  const visit = useVisit()

  const { tabs: tabsToRender } = getContentWithFeatureFlags(flags, visit)

  const { visitId, visitTab } = useParams()

  const activeTab = (visitTab && tabsToRender.find((t) => t?.key === visitTab)) || tabsToRender[0]
  const isTabletOrMobile = useMediaQuery({ query: `(max-width: ${DASHBOARD}px)` })
  const isChatVisit = modality === 'chat'
  const showChatTab = inProgress && isChatVisit && isTabletOrMobile

  useEffect(() => {
    if (isChatVisit && isTabletOrMobile) {
      hideChat()
    }
  }, [activeTab?.label])

  return (
    <ThemeProvider theme={overrideTheme}>
      <div className="flex sm:sticky sm:-top-0 z-10 bg-white">
        <Tabs
          value={activeTab.key}
          variant="scrollable"
          scrollButtons="auto"
          classes={{
            root: 'border-t-0 w-full',
            indicator: 'h-1 bg-secondary-700',
          }}
        >
          {tabsToRender.map((tab) =>
            tab ? (
              <Tab
                component={Link}
                to={`/visits/${visitId}/${tab.key}`}
                label={tab.label}
                value={tab.key}
                disabled={tab.isActiveVisitDependent && !areAdditionalTabsVisible}
                key={tab.key}
              />
            ) : null,
          )}
        </Tabs>
        {showChatTab ? (
          <button
            type="button"
            className={classNames(
              'min-w-[160px] flex justify-center items-center h-12 border-solid border border-neutral-200 p-3 font-sans shadow-[0px_0px_3px_rgba(32,70,103,0.35)]',
              chatDialogVisible ? 'bg-neutral-900 text-secondary-700' : 'bg-pantone-200 text-pantone-800',
            )}
            onClick={toggleChat}
          >
            <ChatIcon aria-hidden="true" className="mx-2" />
            {chatDialogVisible ? 'HIDE CHAT' : 'SHOW CHAT'}
          </button>
        ) : null}
      </div>
      <div
        className={
          TABCONTENT_CLASSNAMES[activeTab.key] ? TABCONTENT_CLASSNAMES[activeTab.key] : 'px-4 sm:px-10 lg:px-20 py-10'
        }
      >
        <Suspense fallback={'Loading tab...'}>{activeTab.component}</Suspense>
      </div>
    </ThemeProvider>
  )
}
TabPanel.propTypes = {
  visitType: PropTypes.oneOf(['medical', 'therapy', 'psychiatry', 'hc', 'success_coaching', 'crisis']),
  areAdditionalTabsVisible: PropTypes.bool,
  chatDialogVisible: PropTypes.bool,
  toggleChat: PropTypes.func,
  hideChat: PropTypes.func,
  modality: PropTypes.string,
}
TabPanel.defaultProps = {
  areAdditionalTabsVisible: false,
}

export default observer(TabPanel)
